import type {
  AnySource,
  ScanSummary,
  SourceKind,
  SourceRanked,
} from "../types.js";
import { GraphBuilder } from "./build-graph.js";
import { computeScanHealth } from "./scan-health.js";
import type { SourceFilters } from "./filter-sources.js";
import { filterSources } from "./filter-sources.js";
import { findAnySources } from "./classify-any-sources.js";
import type { SerializedGraph } from "./graph-types.js";
import {
  countProjectSourceFiles,
  createProgramForDirectory,
  resolveScanRoot,
} from "./load-project.js";

export interface ScanOptions extends SourceFilters {
  targetPath: string;
  json?: boolean;
  dumpGraph?: boolean;
  /** Limit display / JSON list fields to N rows; full scan is computed first. */
  top?: number;
  maxFiles?: number;
}

/**
 * Build `ScanOptions` for `exactOptionalPropertyTypes`: only set keys when arguments are defined
 * (avoid `{ sourceKinds: undefined }` object literals).
 */
export function buildScanOptions(
  targetPath: string,
  sourceKinds?: SourceKind[],
  ignoreGlobs?: string[],
  top?: number,
  maxFiles?: number,
): ScanOptions {
  const o: ScanOptions = { targetPath };
  if (sourceKinds !== undefined) o.sourceKinds = sourceKinds;
  if (ignoreGlobs !== undefined) o.ignoreGlobs = ignoreGlobs;
  if (top !== undefined) o.top = top;
  if (maxFiles !== undefined) o.maxFiles = maxFiles;
  return o;
}

export type ScanResult = ScanSummary | SerializedGraph;

export interface FullScanResult {
  summary: ScanSummary;
  serializedGraph: SerializedGraph;
}

function sourceKey(
  s: Pick<AnySource, "filePath" | "line" | "column" | "name">,
): string {
  return `${s.filePath}:${s.line}:${s.column}:${s.name}`;
}

/**
 * Classifier rows that never matched a graph node (e.g. reporting position mismatch) still appear with blast 0.
 */
function mergeRankedWithOrphans(
  ranked: SourceRanked[],
  allSources: AnySource[],
): SourceRanked[] {
  const keys = new Set(ranked.map(sourceKey));
  const out: SourceRanked[] = ranked.map((r, i) => ({ ...r, rank: i + 1 }));
  let nextRank = out.length + 1;
  const extras = allSources
    .filter((s) => !keys.has(sourceKey(s)))
    .sort(
      (a, b) =>
        a.filePath.localeCompare(b.filePath) ||
        a.line - b.line ||
        a.column - b.column ||
        a.name.localeCompare(b.name),
    );
  for (const s of extras) {
    out.push({
      rank: nextRank++,
      blastRadius: 0,
      graphNodeId: "",
      filePath: s.filePath,
      line: s.line,
      column: s.column,
      name: s.name,
      sourceKind: s.sourceKind,
    });
  }
  return out;
}

/**
 * Limit table / JSON list views. Does not change totals (`sources`, `infectedNodeCount`).
 * CI `--fail-coverage` must run on the **untruncated** summary from `runFullScan`.
 */
export function applyTopToScanSummary(
  summary: ScanSummary,
  top?: number,
): ScanSummary {
  if (top === undefined || top <= 0) return summary;
  const ranked = summary.sourcesRankedByBlast
    .slice(0, top)
    .map((r, i): SourceRanked => ({ ...r, rank: i + 1 }));
  return {
    ...summary,
    sourcesRankedByBlast: ranked,
    greedyCoverPicks: summary.greedyCoverPicks.slice(0, top),
  };
}

/**
 * Single graph build: summary + serialized graph (for DOT / `--dump-graph` / CI).
 */
export function runFullScan(options: ScanOptions): FullScanResult {
  const root = resolveScanRoot(options.targetPath);
  const programOpts =
    options.maxFiles !== undefined ? { maxFiles: options.maxFiles } : undefined;
  const program = createProgramForDirectory(root, programOpts);
  let sources = findAnySources(program, root);
  sources = filterSources(sources, options);
  const fileCount = countProjectSourceFiles(program);

  const builder = new GraphBuilder(program, root);
  builder.build();
  builder.applySources(sources);
  builder.propagate();

  const blastRanked = builder.rankSourcesByBlast();
  const infectedNodeCount = builder.getInfectedNodeCount();
  const greedyCoverPicks = builder.greedySetCoverPicks(blastRanked);

  const ranked = mergeRankedWithOrphans(blastRanked, sources);

  const summary: ScanSummary = {
    sources,
    fileCount,
    infectedNodeCount,
    greedyCoverPicks,
    sourcesRankedByBlast: ranked,
    health: computeScanHealth(builder, {
      sources,
      fileCount,
      infectedNodeCount,
      greedyCoverPicks,
      sourcesRankedByBlast: ranked,
    }),
  };

  return { summary, serializedGraph: builder.serialize() };
}

/**
 * Classify `any` sources (m2), build the project flow graph + propagation + blast ranking (m4), optional graph JSON (m3).
 */
export function classifyScan(
  options: ScanOptions & { dumpGraph: true },
): SerializedGraph;
export function classifyScan(options?: ScanOptions): ScanSummary;
export function classifyScan(
  options: ScanOptions = { targetPath: "." },
): ScanResult {
  if (options.dumpGraph) {
    const { top, dumpGraph: _dumpGraph, ...rest } = options;
    void top;
    return runFullScan(rest).serializedGraph;
  }
  const { top, ...rest } = options;
  const { summary } = runFullScan(rest);
  return applyTopToScanSummary(summary, top);
}
